import React, { useEffect, useState, useCallback } from "react";
import {
  ArrowRightLeft, Loader2, Download, Calendar, X, Filter,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
  ArrowDownCircle, RefreshCw, IndianRupee, Plus, Wallet,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Badge } from "@/components/DashLayout";
import DealerWalletModal from "./DealerWalletModal";

const fmt = (v) =>
  `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const REF_LABELS = { top_up: "Top-up", refund: "Refund" };
const PAGE_SIZES = [25, 50, 100];

export default function BalanceTransfers() {
  const [data, setData] = useState({
    items: [], total: 0, page: 1, limit: 25,
    dealers: [], grand_total: 0, grand_count: 0,
  });
  const [loading, setLoading] = useState(true);
  const [dealerFilter, setDealerFilter] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Dealer Wallet Modal State
  const [showSelectModal, setShowSelectModal] = useState(false);
  const [selectedDealerId, setSelectedDealerId] = useState("");
  const [manageDealer, setManageDealer] = useState(null);

  const handleOpenWallet = (e) => {
    e.preventDefault();
    if (!selectedDealerId) return toast.error("Please select a dealer");
    const d = data.dealers.find(x => x.id === selectedDealerId);
    if (!d) return toast.error("Dealer not found");
    setShowSelectModal(false);
    setManageDealer(d);
    setSelectedDealerId("");
  };

  const total = data.total;
  const pageCount = Math.max(1, Math.ceil(total / perPage));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", page);
      params.set("limit", perPage);
      if (dealerFilter) params.set("dealer_id", dealerFilter);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      const { data: res } = await api.get(`/admin/wallet/credit-report?${params.toString()}`);
      setData(res);
    } catch {}
    setLoading(false);
  }, [page, perPage, dealerFilter, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (page > pageCount && pageCount > 0) setPage(pageCount);
  }, [pageCount, page]);

  const pageTotal = data.items.reduce((s, r) => s + (r.amount || 0), 0);
  const startRow = total > 0 ? (page - 1) * perPage + 1 : 0;
  const endRow = Math.min(page * perPage, total);

  // CSV export
  const handleExportCSV = () => {
    if (!data.items.length) return;
    const headers = ["#", "Date", "Dealer", "Mobile", "Type", "Amount (₹)", "Balance After (₹)", "Note"];
    const rows = data.items.map((r, i) => [
      i + 1,
      r.date ? `"${new Date(r.date).toLocaleString("en-IN")}"` : "",
      `"${r.dealer_name}"`, r.dealer_mobile,
      REF_LABELS[r.reference_type] || r.type,
      r.amount.toFixed(2),
      r.balance_after.toFixed(2),
      `"${(r.note || "").replace(/"/g, '""')}"`,
    ]);
    const csv = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `balance_transfers_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Pagination page numbers
  const visiblePages = (() => {
    const arr = [];
    let lo = Math.max(1, page - 2);
    let hi = Math.min(pageCount, page + 2);
    if (hi - lo < 4) {
      if (lo === 1) hi = Math.min(pageCount, lo + 4);
      else lo = Math.max(1, hi - 4);
    }
    for (let i = lo; i <= hi; i++) arr.push(i);
    return arr;
  })();

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h3 className="font-heading font-bold text-2xl text-brand-nav flex items-center gap-2">
            <ArrowRightLeft className="w-6 h-6 text-brand-primary" />
            Balance Transfers
          </h3>
          <p className="text-sm text-slate-400 mt-0.5">History of all credits given to dealers' wallets</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowSelectModal(true)}
            className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-white font-semibold px-4 py-2 rounded-md transition-colors">
            <Wallet className="w-4 h-4" /> Manage Dealer Wallet
          </button>
          <button onClick={handleExportCSV} disabled={!data.items.length}
            className="inline-flex items-center gap-2 bg-brand-accent hover:bg-brand-accentHover text-white font-semibold px-4 py-2 rounded-md disabled:opacity-40 transition-colors">
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5">
        <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl p-5 text-white shadow-lg">
          <p className="text-xs font-semibold opacity-80 uppercase tracking-wider">Total Transferred</p>
          <p className="font-heading font-bold text-3xl mt-1">{fmt(data.grand_total)}</p>
          <p className="text-xs opacity-70 mt-1">{data.grand_count} transfer{data.grand_count !== 1 ? "s" : ""} total</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <p className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Matching Entries</p>
          <p className="font-heading font-bold text-2xl text-brand-nav mt-1">{total}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <p className="text-[10px] text-green-500 uppercase font-bold tracking-wider">Page Total</p>
          <p className="font-heading font-bold text-2xl text-green-600 mt-1">{fmt(pageTotal)}</p>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 flex flex-wrap items-center gap-4">
        <Filter className="w-4 h-4 text-slate-400 shrink-0" />
        <select value={dealerFilter} onChange={e => { setDealerFilter(e.target.value); setPage(1); }}
          className="text-sm border border-slate-200 rounded-lg px-3 py-2 text-slate-600 bg-white min-w-[180px]">
          <option value="">All Dealers</option>
          {data.dealers?.map(d => (
            <option key={d.id} value={d.id}>{d.name} - {d.mobile}</option>
          ))}
        </select>
        <div className="flex items-center gap-2">
          <Calendar className="w-3.5 h-3.5 text-slate-400 shrink-0" />
          <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }}
            className="text-sm border border-slate-200 rounded-lg px-2 py-1.5 text-slate-600 bg-white" />
          <span className="text-xs text-slate-400">to</span>
          <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }}
            className="text-sm border border-slate-200 rounded-lg px-2 py-1.5 text-slate-600 bg-white" />
          {(dateFrom || dateTo) && (
            <button onClick={() => { setDateFrom(""); setDateTo(""); setPage(1); }}
              className="p-1 text-slate-400 hover:text-red-500 rounded transition-colors" title="Clear dates">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400">Rows:</span>
          <select value={perPage} onChange={e => { setPerPage(Number(e.target.value)); setPage(1); }}
            className="text-sm border border-slate-200 rounded-lg px-2 py-1.5 text-slate-600 bg-white">
            {PAGE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Loading...
        </div>
      ) : data.items.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-400">
          <ArrowRightLeft className="w-8 h-8 mx-auto mb-3 opacity-30" />
          No balance transfers found.{(dateFrom || dateTo) ? " Try adjusting your date filter." : ""}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3">#</th>
                  <th className="text-left px-4 py-3">Date & Time</th>
                  <th className="text-left px-4 py-3">Dealer</th>
                  <th className="text-left px-4 py-3">Type</th>
                  <th className="text-right px-4 py-3 bg-green-50/50">Amount (₹)</th>
                  <th className="text-right px-4 py-3">Balance After (₹)</th>
                  <th className="text-left px-4 py-3">Note</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((r, i) => (
                  <tr key={r.id || i} className="border-t border-slate-100 hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-3 text-slate-400 font-mono text-xs">{startRow + i}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-600">
                      {r.date ? (
                        <>
                          <span className="font-medium">
                            {new Date(r.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                          </span>
                          <span className="text-slate-400 ml-1">
                            {new Date(r.date).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                          </span>
                        </>
                      ) : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-brand-nav text-xs">{r.dealer_name}</div>
                      <div className="text-[10px] text-slate-400 font-mono">{r.dealer_mobile}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        {r.type === "credit" && <ArrowDownCircle className="w-3.5 h-3.5 text-green-500" />}
                        {r.type === "refund" && <RefreshCw className="w-3.5 h-3.5 text-blue-500" />}
                        <Badge tone={r.type === "refund" ? "info" : "success"}>
                          {REF_LABELS[r.reference_type] || r.type}
                        </Badge>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-xs bg-green-50/30">
                      <span className="font-bold text-green-600 flex items-center justify-end gap-0.5">
                        <IndianRupee className="w-3 h-3" />
                        {Number(r.amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-xs font-medium text-slate-600">
                      {fmt(r.balance_after)}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500 max-w-[200px] truncate" title={r.note || ""}>
                      {r.note || "—"}
                    </td>
                  </tr>
                ))}
                {/* Page total row */}
                <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold text-xs">
                  <td colSpan={4} className="px-4 py-3 text-right text-slate-500 uppercase tracking-wider">Page Total</td>
                  <td className="px-4 py-3 text-right font-mono text-green-700 bg-green-50/50 text-sm">
                    +{fmt(pageTotal)}
                  </td>
                  <td colSpan={2}></td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pageCount > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-slate-100 bg-slate-50/50">
              <p className="text-xs text-slate-400 order-2 sm:order-1">
                Showing <span className="font-semibold text-slate-600">{startRow}–{endRow}</span> of{" "}
                <span className="font-semibold text-slate-600">{total}</span> entries
                {" "}· Page <span className="font-semibold text-slate-600">{page}</span> of {pageCount}
              </p>
              <div className="flex items-center gap-1 order-1 sm:order-2">
                <button onClick={() => setPage(1)} disabled={page === 1}
                  className="p-1.5 rounded-md hover:bg-slate-200 disabled:opacity-30 transition-colors" title="First">
                  <ChevronsLeft className="w-3.5 h-3.5 text-slate-500" />
                </button>
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="p-1.5 rounded-md hover:bg-slate-200 disabled:opacity-30 transition-colors" title="Previous">
                  <ChevronLeft className="w-3.5 h-3.5 text-slate-500" />
                </button>
                {visiblePages[0] > 1 && <span className="text-xs text-slate-400 px-1">…</span>}
                {visiblePages.map(p => (
                  <button key={p} onClick={() => setPage(p)}
                    className={`min-w-[28px] h-7 rounded-md text-xs font-semibold transition-colors ${
                      p === page ? "bg-brand-primary text-white shadow-sm" : "text-slate-500 hover:bg-slate-200"
                    }`}>
                    {p}
                  </button>
                ))}
                {visiblePages[visiblePages.length - 1] < pageCount && <span className="text-xs text-slate-400 px-1">…</span>}
                <button onClick={() => setPage(p => Math.min(pageCount, p + 1))} disabled={page === pageCount}
                  className="p-1.5 rounded-md hover:bg-slate-200 disabled:opacity-30 transition-colors" title="Next">
                  <ChevronRight className="w-3.5 h-3.5 text-slate-500" />
                </button>
                <button onClick={() => setPage(pageCount)} disabled={page === pageCount}
                  className="p-1.5 rounded-md hover:bg-slate-200 disabled:opacity-30 transition-colors" title="Last">
                  <ChevronsRight className="w-3.5 h-3.5 text-slate-500" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Dealer Select Modal */}
      {showSelectModal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setShowSelectModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-heading font-bold text-lg text-brand-nav flex items-center gap-2">
                <Wallet className="w-5 h-5 text-brand-primary" />
                Select Dealer to Manage
              </h3>
              <button onClick={() => setShowSelectModal(false)} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            <form onSubmit={handleOpenWallet} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Search and select dealer</label>
                <select required value={selectedDealerId} onChange={e => setSelectedDealerId(e.target.value)}
                  className="w-full border border-slate-200 rounded-lg px-3 py-3 text-sm focus:ring-2 focus:ring-brand-primary/30 outline-none font-medium text-slate-700">
                  <option value="">-- Choose a Dealer --</option>
                  {data.dealers?.map(d => (
                    <option key={d.id} value={d.id}>{d.name} - {d.mobile}</option>
                  ))}
                </select>
                <p className="text-[10px] text-slate-400 mt-2">
                  Select a dealer to top-up, deduct, or view their wallet history.
                </p>
              </div>
              <button type="submit" disabled={!selectedDealerId}
                className="w-full flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-white font-bold py-3 rounded-lg transition-colors disabled:opacity-50">
                Open Wallet
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Full Dealer Wallet Modal */}
      {manageDealer && (
        <DealerWalletModal 
          dealer={manageDealer} 
          dealerUser={manageDealer} 
          onClose={() => { setManageDealer(null); load(); }} 
        />
      )}
    </div>
  );
}
